/**
 * ConditionsVenteScreen — Conditions Générales de Vente (CGV)
 *
 * Encadre la vente des abonnements (Basic / Premium / Gold) et des options
 * ponctuelles (add-ons : essais virtuels, analyses IA, emplacements dressing).
 * Couvre : produits, prix, commande, paiement (Stripe web + IAP mobile),
 * auto-renouvellement, changement de formule, droit de rétractation,
 * remboursement, résiliation, service client, médiation, loi applicable.
 *
 * IMPORTANT : avant publication, faire valider le texte par un avocat ou
 * un service comme Legalstart / Captain Contrat pour la France. Les CGV
 * d'un service à abonnement récurrent comportent des mentions obligatoires
 * (Code de la consommation art. L. 221-5, L. 215-1, etc.).
 */
import React, { useEffect } from 'react';
import {
  View, Text, ScrollView, StyleSheet, SafeAreaView,
  StatusBar, Platform, useWindowDimensions,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import BackButton from '../components/BackButton';
import { openLegalUrl } from '../utils/openLegalUrl';

export default function ConditionsVenteScreen({ navigation }) {
  const { dark, theme } = useTheme();
  const { lang } = useI18n();
  const en = lang === 'en';
  const s = makeStyles(theme);
  const { height: winHeight } = useWindowDimensions();

  useEffect(() => {
    openLegalUrl('cgv', lang);
    const t = setTimeout(() => { try { navigation?.goBack?.(); } catch {} }, 400);
    return () => clearTimeout(t);
  }, [lang, navigation]);

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle={dark ? 'light-content' : 'dark-content'} />

      <View style={s.header}>
        <BackButton onPress={() => {
          try {
            if (navigation.canGoBack && navigation.canGoBack()) navigation.goBack();
            else navigation.navigate('Main');
          } catch { try { navigation.navigate('Main'); } catch {} }
        }} />
        <Text style={s.title}>{en ? 'Terms of Sale' : 'Conditions Générales de Vente'}</Text>
        <View style={{ width: 38 }} />
      </View>

      <ScrollView
        contentContainerStyle={[s.content, { paddingBottom: 80 }]}
        style={Platform.OS === 'web' ? { height: winHeight - 60, overflowY: 'auto', WebkitOverflowScrolling: 'touch' } : undefined}
      >
        <Text style={s.lastUpdate}>{en ? 'Last updated: May 21, 2026' : 'Dernière mise à jour : 21 mai 2026'}</Text>

        <Text style={s.intro}>
          {en
            ? `These Terms of Sale ("Terms of Sale") govern the sale of paid subscriptions and one-off options offered within the MyTenue mobile and web application. They supplement the Terms of Service. Any paid order implies full and unreserved acceptance of these Terms of Sale.`
            : `Les présentes Conditions Générales de Vente (« CGV ») encadrent la vente des abonnements payants et des options ponctuelles proposés au sein de l'application mobile et web MyTenue. Elles complètent les Conditions Générales d'Utilisation. Toute commande payante implique l'acceptation pleine et entière des présentes CGV.`}
        </Text>

        <Section title={en ? '1. Seller' : '1. Vendeur'}>
          <Para>
            {en
              ? `The paid services of the MyTenue application and website (www.mytenue.com) are sold by:`
              : `Les services payants de l'application et du site web MyTenue (www.mytenue.com) sont vendus par :`}
          </Para>
          <Para><Text style={s.bold}>TO BE SMART</Text>{en ? ' — SAS with share capital of EUR 1' : ' — SAS au capital de 1 €'}</Para>
          <Para><Text style={s.bold}>{en ? 'Registered office:' : 'Siège social :'}</Text> 17 rue Benoit Malon, 92150 Suresnes, France</Para>
          <Para><Text style={s.bold}>SIRET:</Text> 928 276 971 00027</Para>
          <Para><Text style={s.bold}>{en ? 'Contact:' : 'Contact :'}</Text> contact@mytenue.com — +33 7 87 45 47 32</Para>
        </Section>

        <Section title={en ? '2. Products and services' : '2. Produits et services'}>
          <Para>
            {en
              ? `MyTenue offers a free version with usage limits and the following paid offers:`
              : `MyTenue propose une version gratuite avec des limites d'utilisation et les offres payantes suivantes :`}
          </Para>
          <Para><Text style={s.bold}>{en ? 'Recurring subscriptions' : 'Abonnements récurrents'}</Text></Para>
          <Bullet><Text style={s.bold}>Basic</Text>{en ? ' — extended wardrobe, AI suggestions and virtual try-ons with higher limits' : ' — dressing étendu, suggestions IA et essais virtuels avec des limites supérieures'}</Bullet>
          <Bullet><Text style={s.bold}>Premium</Text>{en ? ' — generous limits, priority styling and travel mode' : ' — limites généreuses, stylisme prioritaire et mode voyage'}</Bullet>
          <Bullet><Text style={s.bold}>Gold</Text>{en ? ' — maximum limits, full features and VIP support' : ' — limites maximales, fonctionnalités complètes et support VIP'}</Bullet>
          <Para style={{ marginTop: 6 }}>
            {en
              ? `Each subscription is available on a monthly or annual basis. The detailed content of each plan is displayed in the Application at the time of purchase.`
              : `Chaque abonnement est disponible en formule mensuelle ou annuelle. Le détail de chaque formule est affiché dans l'Application au moment de l'achat.`}
          </Para>
          <Para><Text style={s.bold}>{en ? 'One-off add-ons' : 'Options ponctuelles (add-ons)'}</Text></Para>
          <Bullet>{en ? 'Additional virtual try-on packs' : "Packs d'essais virtuels supplémentaires"}</Bullet>
          <Bullet>{en ? 'Additional AI analysis (scan) packs' : "Packs d'analyses IA (scans) supplémentaires"}</Bullet>
          <Bullet>{en ? 'Additional wardrobe slots' : 'Emplacements de dressing supplémentaires'}</Bullet>
          <Para style={{ marginTop: 6 }}>
            {en
              ? `Add-ons are purchased once. Consumable add-ons (try-ons, scans) remain valid for thirty (30) days from the date of purchase; unused credit expires at the end of this period.`
              : `Les add-ons sont achetés à l'unité. Les add-ons consommables (essais, analyses) sont valables trente (30) jours à compter de la date d'achat ; le crédit non consommé expire à l'issue de cette période.`}
          </Para>
        </Section>

        <Section title={en ? '3. Prices' : '3. Prix'}>
          <Para>
            {en
              ? `Prices are indicated in euros (EUR), all taxes included (VAT applicable). The price applicable to an order is the one displayed in the Application at the time of purchase confirmation.`
              : `Les prix sont indiqués en euros (EUR), toutes taxes comprises (TVA applicable). Le prix applicable à une commande est celui affiché dans l'Application au moment de la confirmation d'achat.`}
          </Para>
          <Para>
            {en
              ? `MyTenue reserves the right to modify its prices at any time. A price change does not apply to current subscriptions until their next renewal; you are informed of any change before it takes effect and may cancel before renewal if you do not accept it.`
              : `MyTenue se réserve le droit de modifier ses prix à tout moment. Une modification de prix ne s'applique pas aux abonnements en cours avant leur prochain renouvellement ; vous êtes informé(e) de toute modification avant son entrée en vigueur et pouvez résilier avant le renouvellement si vous ne l'acceptez pas.`}
          </Para>
        </Section>

        <Section title={en ? '4. Order and payment' : '4. Commande et paiement'}>
          <Para>
            <Text style={s.bold}>{en ? 'Web version (www.mytenue.com):' : 'Version web (www.mytenue.com) :'}</Text> {en
              ? `payments are processed by Stripe Payments Europe, a secure payment service provider certified PCI-DSS Level 1. MyTenue never stores your bank card details.`
              : `les paiements sont traités par Stripe Payments Europe, prestataire de paiement sécurisé certifié PCI-DSS niveau 1. MyTenue ne conserve jamais les coordonnées de votre carte bancaire.`}
          </Para>
          <Para>
            <Text style={s.bold}>{en ? 'Mobile applications:' : 'Applications mobiles :'}</Text> {en
              ? `purchases are made through the App Store (iOS) or Google Play (Android) and are subject to the general terms of those platforms.`
              : `les achats sont effectués via l'App Store (iOS) ou Google Play (Android) et sont soumis aux conditions générales de ces plateformes.`}
          </Para>
          <Para>
            {en
              ? `The order is final once payment is confirmed. A confirmation is then displayed in the Application and, where applicable, sent by email.`
              : `La commande est ferme et définitive dès la confirmation du paiement. Une confirmation est alors affichée dans l'Application et, le cas échéant, envoyée par e-mail.`}
          </Para>
        </Section>

        <Section title={en ? '5. Term and auto-renewal' : '5. Durée et renouvellement automatique'}>
          <Para>
            {en
              ? `Subscriptions are concluded for the chosen period (one month or one year) and are renewed automatically by tacit agreement for an identical period, unless cancelled before the end of the current period.`
              : `Les abonnements sont conclus pour la période choisie (un mois ou un an) et sont renouvelés automatiquement par tacite reconduction pour une période identique, sauf résiliation avant la fin de la période en cours.`}
          </Para>
          <Para>
            {en
              ? `In accordance with article L. 215-1 of the French Consumer Code, you are informed by any means of the renewal date and may oppose it at any time, free of charge, by cancelling under the conditions of article 7.`
              : `Conformément à l'article L. 215-1 du Code de la consommation, vous êtes informé(e) par tout moyen de la date de reconduction et pouvez vous y opposer à tout moment, sans frais, en résiliant dans les conditions de l'article 7.`}
          </Para>
        </Section>

        <Section title={en ? '6. Plan changes' : '6. Changement de formule'}>
          <Para>
            {en
              ? `You may change your plan at any time. The rule is one single active subscription at a time:`
              : `Vous pouvez changer de formule à tout moment. La règle est celle d'un seul abonnement actif à la fois :`}
          </Para>
          <Bullet>{en ? 'An upgrade (e.g. Basic → Premium) takes effect immediately. Stripe calculates a prorated amount (credit for the unused days of the previous plan, charge for the new plan); only the difference is charged.' : "Une montée en gamme (ex. Basic → Premium) prend effet immédiatement. Stripe calcule un prorata (crédit pour les jours non consommés de l'ancienne formule, facturation de la nouvelle) ; seule la différence est débitée."}</Bullet>
          <Bullet>{en ? 'A downgrade (e.g. Premium → Basic) or a switch from annual to monthly takes effect at the end of the period already paid for, with no immediate charge.' : "Une descente en gamme (ex. Premium → Basic) ou un passage de l'annuel au mensuel prend effet à la fin de la période déjà payée, sans débit immédiat."}</Bullet>
          <Bullet>{en ? 'A switch from monthly to annual: the annual amount is charged immediately, your remaining monthly days are preserved, and the annual subscription starts at the end of your current monthly period.' : "Un passage du mensuel à l'annuel : le montant annuel est débité immédiatement, vos jours mensuels restants sont préservés et l'abonnement annuel démarre à la fin de votre période mensuelle en cours."}</Bullet>
        </Section>

        <Section title={en ? '7. Cancellation' : '7. Résiliation'}>
          <Para>
            {en
              ? `Web subscriptions can be cancelled at any time from the Profile screen of the Application. Mobile subscriptions are cancelled in your App Store or Google Play account settings.`
              : `Les abonnements web peuvent être résiliés à tout moment depuis l'écran Profil de l'Application. Les abonnements mobiles se résilient dans les réglages de votre compte App Store ou Google Play.`}
          </Para>
          <Para>
            {en
              ? `Cancellation takes effect at the end of the current billing period: no further charge is made, and you keep access to your plan until that date. After expiry, the account automatically reverts to the free version.`
              : `La résiliation prend effet à la fin de la période de facturation en cours : aucun débit supplémentaire n'est effectué et vous conservez l'accès à votre formule jusqu'à cette date. Après expiration, le compte bascule automatiquement sur la version gratuite.`}
          </Para>
        </Section>

        <Section title={en ? '8. Right of withdrawal' : '8. Droit de rétractation'}>
          <Para>
            {en
              ? `The paid services of MyTenue consist of digital content supplied without a tangible medium, the performance of which begins immediately after the order.`
              : `Les services payants de MyTenue constituent un contenu numérique fourni sans support matériel, dont l'exécution commence immédiatement après la commande.`}
          </Para>
          <Para>
            {en
              ? `In accordance with article L. 221-28 13° of the French Consumer Code, by confirming your order you expressly request immediate access to the service and acknowledge that you thereby waive your 14-day right of withdrawal once performance has begun.`
              : `Conformément à l'article L. 221-28 13° du Code de la consommation, en confirmant votre commande vous demandez expressément l'accès immédiat au service et reconnaissez renoncer ainsi à votre droit de rétractation de 14 jours dès lors que l'exécution a commencé.`}
          </Para>
          <Para>
            {en
              ? `This legal exception does not affect the commercial gestures described in article 9.`
              : `Cette exception légale est sans préjudice des gestes commerciaux décrits à l'article 9.`}
          </Para>
        </Section>

        <Section title={en ? '9. Refunds' : '9. Remboursement'}>
          <Para>
            {en
              ? `For purchases made on the web version (Stripe), any refund request must be addressed to contact@mytenue.com. MyTenue may, as a commercial gesture, grant a full or prorated refund, in particular in the event of a duplicate charge, a technical malfunction preventing use of the service, or an error attributable to MyTenue.`
              : `Pour les achats effectués sur la version web (Stripe), toute demande de remboursement doit être adressée à contact@mytenue.com. MyTenue peut, à titre de geste commercial, accorder un remboursement total ou au prorata, notamment en cas de double facturation, de dysfonctionnement technique empêchant l'usage du service, ou d'erreur imputable à MyTenue.`}
          </Para>
          <Para>
            {en
              ? `For purchases made on the mobile applications, refund requests are handled directly and exclusively by Apple (App Store) or Google (Play Store) according to their own policies.`
              : `Pour les achats effectués sur les applications mobiles, les demandes de remboursement sont traitées directement et exclusivement par Apple (App Store) ou Google (Play Store) selon leurs propres politiques.`}
          </Para>
          <Para>
            {en
              ? `When a plan change leads to the cancellation of a prepaid option, the corresponding amount is automatically refunded via Stripe.`
              : `Lorsqu'un changement de formule entraîne l'annulation d'une option prépayée, le montant correspondant est automatiquement remboursé via Stripe.`}
          </Para>
        </Section>

        <Section title={en ? '10. Service availability' : '10. Disponibilité du service'}>
          <Para>
            {en
              ? `MyTenue undertakes to provide the service with reasonable care but cannot guarantee uninterrupted, error-free availability. Scheduled maintenance, technical incidents or force majeure may temporarily affect access. Such interruptions do not give rise to compensation, without prejudice to article 9 in the event of a prolonged malfunction.`
              : `MyTenue s'engage à fournir le service avec un soin raisonnable mais ne peut garantir une disponibilité ininterrompue et sans erreur. Une maintenance programmée, un incident technique ou un cas de force majeure peuvent affecter temporairement l'accès. De telles interruptions n'ouvrent pas droit à indemnisation, sans préjudice de l'article 9 en cas de dysfonctionnement prolongé.`}
          </Para>
        </Section>

        <Section title={en ? '11. Customer service' : '11. Service client'}>
          <Para>
            {en
              ? `For any question or complaint relating to an order, you can contact our customer service at:`
              : `Pour toute question ou réclamation relative à une commande, vous pouvez contacter notre service client à :`}
          </Para>
          <Text style={s.contact}>contact@mytenue.com</Text>
        </Section>

        <Section title={en ? '12. Mediation and applicable law' : '12. Médiation et loi applicable'}>
          <Para>
            {en
              ? `These Terms of Sale are governed by French law. In accordance with article L. 612-1 of the French Consumer Code, in the event of an unresolved dispute you may resort free of charge to a consumer mediator. The European Commission also provides an online dispute resolution platform: ec.europa.eu/consumers/odr.`
              : `Les présentes CGV sont soumises au droit français. Conformément à l'article L. 612-1 du Code de la consommation, en cas de litige non résolu vous pouvez recourir gratuitement à un médiateur de la consommation. La Commission européenne met également à disposition une plateforme de règlement en ligne des litiges : ec.europa.eu/consumers/odr.`}
          </Para>
          <Para>
            {en
              ? `Failing an amicable settlement, any dispute falls within the jurisdiction of the competent French courts, subject to the mandatory provisions applicable to consumers.`
              : `À défaut de règlement amiable, tout litige relève de la compétence des tribunaux français compétents, sous réserve des dispositions impératives applicables aux consommateurs.`}
          </Para>
        </Section>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// Sous-composants
function Section({ title, children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}
function Para({ children, style }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return <Text style={[s.para, style]}>{children}</Text>;
}
function Bullet({ children }) {
  const { theme } = useTheme();
  const s = makeStyles(theme);
  return (
    <View style={s.bulletRow}>
      <Text style={s.bulletDot}>•</Text>
      <Text style={s.bulletText}>{children}</Text>
    </View>
  );
}

const makeStyles = (theme) => StyleSheet.create({
  safe: { flex: 1, backgroundColor: theme.bg },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    paddingTop: Platform.OS === 'android'
      ? (StatusBar.currentHeight || 24) + 8
      : 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.bgBorder,
  },
  title: { fontSize: 16, fontWeight: '600', color: theme.text, letterSpacing: 0.3 },
  content: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 60 },
  lastUpdate: {
    fontSize: 11,
    color: theme.textFaint,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 20,
  },
  intro: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
    marginBottom: 24,
  },
  section: { marginBottom: 24 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.text,
    marginBottom: 10,
    letterSpacing: 0.2,
  },
  para: {
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
    marginBottom: 10,
  },
  bold: { fontWeight: '600', color: theme.text },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 16,
    fontSize: 14,
    color: theme.accent,
    lineHeight: 22,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 22,
    color: theme.textSec,
  },
  contact: {
    fontSize: 14,
    color: theme.accent,
    fontWeight: '500',
    marginTop: 4,
  },
});
